import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { DAYS_PT, MONTHS_PT_LONG } from "../themes/themes";
import {
  getAgendaMes,
  toggleBloqueio,
  bloquearRange,
} from "../services/agendaService";
import Loading, { ErrorBox } from "../components/ui/Loading";

// StatusAgenda: 0 Livre · 1 Vendido · 2 Bloqueado
const STATUS = { LIVRE: 0, VENDIDO: 1, BLOQUEADO: 2 };

function buildCalendar(ano, mes, dias) {
  // dias = AgendaDto[] vindo da API; cada dia pode ter 0..N registros (mas o
  // backend tem unique index por (Guia,Data) — então no máximo 1).
  const byDay = new Map();
  for (const d of dias) {
    const day = parseInt(d.data.split("-")[2], 10);
    byDay.set(day, d);
  }
  const cells = [];
  const firstDay = new Date(ano, mes - 1, 1).getDay();

  // Cells antes do dia 1 mostram o fim do mês anterior em cinza claro
  // (apenas noção de continuidade visual — não-interativas).
  const prevMonthTotal = new Date(ano, mes - 1, 0).getDate();
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevMonthTotal - i, isOutOfMonth: true, status: STATUS.LIVRE });
  }

  const totalDays = new Date(ano, mes, 0).getDate();
  for (let d = 1; d <= totalDays; d++) {
    const reg = byDay.get(d);
    cells.push({
      day: d,
      isOutOfMonth: false,
      status: reg ? reg.status : STATUS.LIVRE,
      cliente: reg?.clienteNome ?? null,
      obs: reg?.observacao ?? null,
      motivoBloqueio: reg?.motivoBloqueio ?? null,
      roteiroTitulo: reg?.roteiroTitulo ?? null,
    });
  }

  // Completa a última semana com dias do mês seguinte pro grid fechar em 7 colunas.
  let nextDay = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ day: nextDay++, isOutOfMonth: true, status: STATUS.LIVRE });
  }
  return cells;
}

function isoDate(ano, mes, dia) {
  const mm = String(mes).padStart(2, "0");
  const dd = String(dia).padStart(2, "0");
  return `${ano}-${mm}-${dd}`;
}

export default function AgendaPage() {
  const { C } = useTheme();
  const today = new Date();
  const [ano, setAno] = useState(today.getFullYear());
  const [mes, setMes] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Estado do modal de bloqueio. `mode`:
  //   "block"   — bloquear um dia livre
  //   "range"   — bloquear um intervalo
  //   "unblock" — confirmar liberação de um dia já bloqueado
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const res = await getAgendaMes(ano, mes);
      setData(res);
      setError(null);
    } catch {
      setError("Não foi possível carregar a agenda.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, [ano, mes]);

  function flashToast(msg, ms = 3000) {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  }

  function handleDayClick(cell) {
    if (!cell.day || cell.isOutOfMonth) return;
    const dataISO = isoDate(ano, mes, cell.day);
    if (cell.status === STATUS.VENDIDO) {
      const nome = cell.roteiroTitulo
        ? `Este dia está vinculado ao roteiro "${cell.roteiroTitulo}", edite o roteiro para liberar`
        : "Dia vendido a um roteiro — edite o roteiro para liberar.";
      flashToast(nome, 3500);
      return;
    }
    if (cell.status === STATUS.BLOQUEADO) {
      setModal({
        mode: "unblock",
        data: dataISO,
        motivo: cell.motivoBloqueio || "",
      });
      return;
    }
    // Dia livre → abre o modal de bloqueio já preenchido com o dia clicado
    // em início/fim. O usuário pode estender `fim` para formar um intervalo.
    setModal({
      mode: "block",
      inicio: dataISO,
      fim: dataISO,
      motivo: "",
    });
  }

  function openRangeModal() {
    const hojeISO = isoDate(
      today.getFullYear(),
      today.getMonth() + 1,
      today.getDate()
    );
    setModal({ mode: "block", inicio: hojeISO, fim: hojeISO, motivo: "" });
  }

  async function confirmBlock() {
    if (!modal) return;
    if (!modal.inicio || !modal.fim) {
      flashToast("Informe data início e fim.");
      return;
    }
    if (modal.fim < modal.inicio) {
      flashToast("Data final precisa ser maior ou igual à inicial.");
      return;
    }
    setSaving(true);
    try {
      // bloquearRange funciona igualmente bem para 1 dia (início == fim) ou
      // para um intervalo — evita duplicar fluxo no frontend.
      const res = await bloquearRange(
        modal.inicio,
        modal.fim,
        modal.motivo?.trim() || null
      );
      setModal(null);
      await reload();
      if (res?.pulados > 0) {
        flashToast(
          `${res.bloqueados} dia(s) bloqueado(s). ${res.pulados} dia(s) ignorado(s) por estarem vendidos.`,
          4000
        );
      } else {
        flashToast(`${res?.bloqueados ?? 0} dia(s) bloqueado(s).`, 2500);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Não foi possível bloquear.";
      flashToast(msg);
    } finally {
      setSaving(false);
    }
  }

  async function confirmUnblock() {
    if (!modal) return;
    setSaving(true);
    try {
      // toggleBloqueio alterna — chamado num dia bloqueado, libera.
      await toggleBloqueio(modal.data, null);
      setModal(null);
      await reload();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Não foi possível liberar esse dia.";
      flashToast(msg);
    } finally {
      setSaving(false);
    }
  }

  const cells = useMemo(
    () => buildCalendar(ano, mes, data?.dias ?? []),
    [ano, mes, data]
  );

  const counts = useMemo(() => {
    const c = { vendidos: 0, bloqueados: 0, livres: 0 };
    for (const cell of cells) {
      if (cell.isOutOfMonth) continue;
      if (cell.status === STATUS.VENDIDO) c.vendidos++;
      else if (cell.status === STATUS.BLOQUEADO) c.bloqueados++;
      else c.livres++;
    }
    return c;
  }, [cells]);

  function navigate(delta) {
    let novoMes = mes + delta;
    let novoAno = ano;
    if (novoMes < 1) {
      novoMes = 12;
      novoAno--;
    } else if (novoMes > 12) {
      novoMes = 1;
      novoAno++;
    }
    setMes(novoMes);
    setAno(novoAno);
  }

  function gotoHoje() {
    const t = new Date();
    setAno(t.getFullYear());
    setMes(t.getMonth() + 1);
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
            Agenda
          </h1>
          <div
            style={{
              fontSize: "clamp(24px, 5vw, 32px)",
              fontWeight: 700,
              color: C.accent,
              margin: "4px 0 12px",
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
            }}
          >
            {MONTHS_PT_LONG[mes - 1]} de {ano}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={openRangeModal} style={btnSecondary(C)}>
            Bloquear período
          </button>
          <button onClick={() => navigate(-1)} style={btnSecondary(C)}>
            ◀ {MONTHS_PT_LONG[((mes - 2) + 12) % 12]}
          </button>
          <button onClick={gotoHoje} style={btnPrimary(C)}>
            Hoje
          </button>
          <button onClick={() => navigate(1)} style={btnSecondary(C)}>
            {MONTHS_PT_LONG[mes % 12]} ▶
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 14,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        {[
          { label: "Vendidos", count: counts.vendidos, color: C.accent },
          { label: "Livres", count: counts.livres, color: C.green },
          { label: "Bloqueados", count: counts.bloqueados, color: C.purple },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              background: C.bgCard,
              borderRadius: 10,
              border: `1px solid ${C.border}`,
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: s.color,
              }}
            />
            <span style={{ fontSize: 12, color: C.textMuted }}>{s.label}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
              {s.count}
            </span>
          </div>
        ))}
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "12px 20px",
            background: C.bgCard,
            border: `1px solid ${C.accent}`,
            borderRadius: 10,
            color: C.text,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            zIndex: 2000,
            fontFamily: "'DM Sans', sans-serif",
            maxWidth: "90vw",
            textAlign: "center",
          }}
        >
          {toast}
        </div>
      )}

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && !error && (
        <div
          style={{
            background: C.bgCard,
            borderRadius: 14,
            padding: 20,
            border: `1px solid ${C.border}`,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 2,
              marginBottom: 8,
            }}
          >
            {DAYS_PT.map((d) => (
              <div
                key={d}
                style={{
                  textAlign: "center",
                  fontSize: 11,
                  color: C.textDim,
                  fontWeight: 600,
                  padding: "6px 0",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                {d}
              </div>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 4,
            }}
          >
            {cells.map((d, i) => (
              <div
                key={i}
                onClick={() => handleDayClick(d)}
                title={
                  d.isOutOfMonth
                    ? ""
                    : d.status === STATUS.VENDIDO
                    ? `Vendido${
                        d.roteiroTitulo ? " — " + d.roteiroTitulo : ""
                      }${d.cliente ? " (" + d.cliente + ")" : ""}`
                    : d.status === STATUS.BLOQUEADO
                    ? `Bloqueado${
                        d.motivoBloqueio ? " — " + d.motivoBloqueio : ""
                      } (clique para liberar)`
                    : "Livre — clique para bloquear"
                }
                style={{
                  aspectRatio: "1",
                  borderRadius: 10,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: d.isOutOfMonth
                    ? "transparent"
                    : d.status === STATUS.VENDIDO
                    ? C.accentDim
                    : d.status === STATUS.BLOQUEADO
                    ? "linear-gradient(135deg, rgba(167,139,250,0.45) 0%, rgba(124,58,237,0.55) 100%)"
                    : C.bgCardHover,
                  border: d.isOutOfMonth
                    ? "none"
                    : d.status === STATUS.VENDIDO
                    ? `1px solid ${C.accent}40`
                    : d.status === STATUS.BLOQUEADO
                    ? `2px solid ${C.purple}`
                    : `1px solid ${C.border}`,
                  cursor: d.isOutOfMonth
                    ? "default"
                    : d.status === STATUS.VENDIDO
                    ? "not-allowed"
                    : "pointer",
                  transition: "all 0.15s",
                  minHeight: 56,
                  padding: "4px 3px",
                  boxShadow:
                    !d.isOutOfMonth && d.status === STATUS.BLOQUEADO
                      ? `0 0 0 1px ${C.purple}55, inset 0 0 12px rgba(124,58,237,0.25)`
                      : "none",
                }}
              >
                {d.day && (
                  <>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: d.isOutOfMonth
                          ? 400
                          : d.status === STATUS.VENDIDO ||
                            d.status === STATUS.BLOQUEADO
                          ? 700
                          : 500,
                        color: d.isOutOfMonth
                          ? C.textDim
                          : d.status === STATUS.VENDIDO
                          ? C.accent
                          : d.status === STATUS.BLOQUEADO
                          ? "#fff"
                          : C.text,
                        textDecoration:
                          !d.isOutOfMonth && d.status === STATUS.BLOQUEADO
                            ? "line-through"
                            : "none",
                        textDecorationColor:
                          !d.isOutOfMonth && d.status === STATUS.BLOQUEADO
                            ? "rgba(255,255,255,0.85)"
                            : undefined,
                        textShadow:
                          !d.isOutOfMonth && d.status === STATUS.BLOQUEADO
                            ? "0 1px 2px rgba(88,28,135,0.6)"
                            : "none",
                        lineHeight: 1,
                      }}
                    >
                      {d.day}
                    </span>
                    {!d.isOutOfMonth && d.cliente && (
                      <span
                        style={{
                          fontSize: 8,
                          color: C.accentLight,
                          marginTop: 2,
                          maxWidth: "90%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        {d.cliente}
                      </span>
                    )}
                    {!d.isOutOfMonth && d.status === STATUS.BLOQUEADO && (
                      <span
                        style={{
                          fontSize: 9,
                          color: "#fff",
                          marginTop: 3,
                          maxWidth: "95%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          fontWeight: 700,
                          lineHeight: 1.1,
                          textShadow: "0 1px 2px rgba(88,28,135,0.7)",
                          letterSpacing: 0.2,
                        }}
                      >
                        {d.motivoBloqueio || "Bloqueado"}
                      </span>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <BloqueioModal
          C={C}
          modal={modal}
          saving={saving}
          onChange={setModal}
          onClose={() => !saving && setModal(null)}
          onConfirm={modal.mode === "unblock" ? confirmUnblock : confirmBlock}
        />
      )}
    </div>
  );
}

function BloqueioModal({ C, modal, saving, onChange, onClose, onConfirm }) {
  const isRange =
    modal.mode === "block" && modal.inicio && modal.fim && modal.inicio !== modal.fim;
  const title =
    modal.mode === "unblock"
      ? "Liberar dia"
      : isRange
      ? "Bloquear período"
      : "Bloquear dia";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 1500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.bgCard,
          borderRadius: 14,
          border: `1px solid ${C.border}`,
          padding: 24,
          width: "100%",
          maxWidth: 440,
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 700,
            color: C.text,
            marginBottom: 16,
          }}
        >
          {title}
        </h2>

        {modal.mode === "block" && (
          <>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle(C)}>Início</label>
                <input
                  autoFocus
                  type="date"
                  value={modal.inicio}
                  onChange={(e) =>
                    onChange({ ...modal, inicio: e.target.value })
                  }
                  style={inputStyle(C)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle(C)}>Fim</label>
                <input
                  type="date"
                  value={modal.fim}
                  onChange={(e) => onChange({ ...modal, fim: e.target.value })}
                  style={inputStyle(C)}
                />
              </div>
            </div>
            <label style={{ ...labelStyle(C), marginTop: 12 }}>
              Motivo (opcional)
            </label>
            <input
              type="text"
              maxLength={200}
              placeholder="Ex: Show, Férias, Compromisso pessoal"
              value={modal.motivo}
              onChange={(e) => onChange({ ...modal, motivo: e.target.value })}
              style={inputStyle(C)}
            />
            <p
              style={{
                color: C.textMuted,
                fontSize: 11,
                margin: "10px 0 0",
              }}
            >
              Para bloquear apenas 1 dia, deixe início e fim iguais. Dias já
              vendidos a roteiros serão ignorados.
            </p>
          </>
        )}

        {modal.mode === "unblock" && (
          <>
            <p style={{ color: C.textMuted, fontSize: 13, margin: "0 0 8px" }}>
              Dia: <strong style={{ color: C.text }}>{modal.data}</strong>
            </p>
            {modal.motivo ? (
              <p
                style={{
                  color: C.text,
                  fontSize: 13,
                  margin: "0 0 14px",
                  padding: "10px 12px",
                  background: C.bgCardHover,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                }}
              >
                Motivo atual: <em>{modal.motivo}</em>
              </p>
            ) : (
              <p style={{ color: C.textMuted, fontSize: 13, margin: "0 0 14px" }}>
                Sem motivo registrado.
              </p>
            )}
            <p style={{ color: C.textMuted, fontSize: 13, margin: "0 0 6px" }}>
              Confirma a liberação deste dia?
            </p>
          </>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 20,
          }}
        >
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            style={btnSecondary(C)}
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onConfirm}
            style={{
              ...btnPrimary(C),
              background:
                modal.mode === "unblock" ? C.green || C.accent : C.accent,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving
              ? "Salvando..."
              : modal.mode === "unblock"
              ? "Liberar"
              : "Bloquear"}
          </button>
        </div>
      </div>
    </div>
  );
}

function labelStyle(C) {
  return {
    display: "block",
    fontSize: 11,
    color: C.textMuted,
    fontWeight: 600,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  };
}

function inputStyle(C) {
  return {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.bgCardHover,
    color: C.text,
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
  };
}

function btnSecondary(C) {
  return {
    padding: "8px 16px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.bgCard,
    color: C.text,
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "'DM Sans', sans-serif",
  };
}
function btnPrimary(C) {
  return {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    background: C.accent,
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
  };
}
